import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea, ReferenceDot, Text,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import {
  BatteryCharging, AlertCircle, AlertTriangle, CheckCircle2, XCircle, Info, Copy, RotateCcw,
  Sparkles, Plus, Trash2,
} from 'lucide-react';
import {
  HV_PRESETS, DEFAULT_COMPONENTS, DEFAULT_INPUTS, EXAMPLE_INPUTS, EXAMPLE_INCLUDED, E_SERIES_OPTIONS,
  NUMERIC_FIELD_SPECS, COMPONENT_FIELD_SPECS, MAX_COMPONENTS, validateField,
} from '../../lib/prechargeTools';
import { prechargeToolApi } from '../../lib/toolsApi';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, CHART_TEXT, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './simulator/ReferenceLineTopLabel';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(NUMERIC_FIELD_SPECS.map((s) => [s.key, s]));
const NUMERIC_KEYS = NUMERIC_FIELD_SPECS.map((s) => s.key);
const MARK_RED = '#dc2626';
const MARK_DARK = '#334155';

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n)
  ? '—'
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtOhm = (r) => (r === null || r === undefined ? '—' : r >= 1000 ? `${fmt(r / 1000, 3)} kΩ` : `${fmt(r, r < 10 ? 3 : r < 100 ? 2 : 1)} Ω`);
const toRaw = (v) => (v === null || v === undefined ? '' : String(v));

let rowSeq = 0;
const makeRows = (list, includedOverrides = {}) => list.map((c) => ({
  id: ++rowSeq,
  included: includedOverrides[c.name] ?? c.included,
  name: c.name,
  qty: toRaw(c.qty),
  xUf: toRaw(c.xUf),
  yPosNf: toRaw(c.yPosNf),
  yNegNf: toRaw(c.yNegNf),
  bleedKohm: toRaw(c.bleedKohm),
}));
const buildRaw = (values) => Object.fromEntries(NUMERIC_KEYS.map((k) => [k, toRaw(values[k])]));

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const ErrorText = ({ children, testId }) => (
  <p className="flex items-center gap-1 text-xs text-destructive" data-testid={testId}>
    <AlertCircle className="h-3 w-3 shrink-0" /> {children}
  </p>
);

const Section = ({ title, children, right }) => (
  <div className="space-y-4 p-4 bg-muted/50 rounded-md">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const PrechargeResistorCalculator = () => {
  const [mode, setMode] = useState(DEFAULT_INPUTS.mode);
  const [criterion, setCriterion] = useState(DEFAULT_INPUTS.criterion);
  const [eSeries, setESeries] = useState(DEFAULT_INPUTS.eSeries);
  const [preset, setPreset] = useState('400v');
  const [raw, setRaw] = useState(() => buildRaw(DEFAULT_INPUTS));
  const [rows, setRows] = useState(() => makeRows(DEFAULT_COMPONENTS));
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const applyInputs = (values, includedOverrides) => {
    setRaw(buildRaw(values));
    setMode(values.mode);
    setCriterion(values.criterion);
    setESeries(values.eSeries);
    setRows(makeRows(DEFAULT_COMPONENTS, includedOverrides));
  };

  const handlePreset = (value) => {
    setPreset(value);
    const p = HV_PRESETS.find((x) => x.value === value);
    if (p) setRaw((prev) => ({ ...prev, ...buildRaw({ ...prev, ...p.values }) }));
  };
  const handleExample = () => {
    applyInputs(EXAMPLE_INPUTS, EXAMPLE_INCLUDED);
    setPreset('800v');
    toast.success('Example values loaded.');
  };
  const handleReset = () => {
    applyInputs(DEFAULT_INPUTS);
    setPreset('400v');
  };

  const updateRow = (id, key, value) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addRow = () => setRows((prev) => (prev.length >= MAX_COMPONENTS ? prev : [
    ...prev,
    { id: ++rowSeq, included: true, name: `Custom component ${prev.length + 1}`, qty: '1', xUf: '10', yPosNf: '10', yNegNf: '10', bleedKohm: '' },
  ]));

  // ---- Client-side validation (instant feedback; the backend re-validates) ----
  const { fieldErrors, payload } = useMemo(() => {
    const errors = {};
    const values = {};
    const needed = new Set(NUMERIC_KEYS.filter((k) => !SPEC_BY_KEY[k].optional));
    needed.delete(mode === 'size' ? 'resistorOhm' : 'targetTimeMs');
    needed.delete(criterion === 'percent' ? 'deltaVV' : 'thresholdPercent');
    for (const key of NUMERIC_KEYS) {
      const spec = SPEC_BY_KEY[key];
      const optional = spec.optional || (key === 'targetTimeMs' && mode === 'check');
      if (!needed.has(key) && !optional) continue;
      const err = validateField({ ...spec, optional }, raw[key]);
      if (err) errors[key] = err;
      else values[key] = raw[key] === '' ? null : Number(raw[key]);
    }
    const comps = rows.map((r, i) => {
      const c = { included: r.included, name: r.name };
      for (const spec of COMPONENT_FIELD_SPECS) {
        const err = validateField(spec, r[spec.key]);
        if (err && r.included) errors[`components.${i}.${spec.key}`] = err;
        c[spec.key] = r[spec.key] === '' ? null : Number(r[spec.key]);
      }
      return c;
    });
    if (!rows.some((r) => r.included)) errors.components = 'Include at least one component.';
    if (values.packVminV && values.packVnomV && values.packVmaxV
      && !(values.packVminV <= values.packVnomV && values.packVnomV <= values.packVmaxV)) {
      errors.voltages = 'Pack voltages must satisfy V_min ≤ V_nom ≤ V_max.';
    }
    if (criterion === 'deltaV' && values.deltaVV && values.packVminV && values.deltaVV >= values.packVminV) {
      errors.deltaVV = `ΔV must be smaller than V_min (${values.packVminV} V).`;
    }
    if (values.battRintMohm === 0 && values.loopRmohm === 0) {
      errors.loopRmohm = 'Battery internal + loop resistance must be greater than 0 (needed for the inrush estimate).';
    }
    return {
      fieldErrors: errors,
      payload: { ...values, mode, criterion, eSeries, components: comps },
    };
  }, [raw, rows, mode, criterion, eSeries]);

  const hasClientErrors = Object.keys(fieldErrors).length > 0;
  const payloadKey = JSON.stringify(payload);

  const [results, setResults] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasClientErrors) {
      setResults(null);
      setServerErrors({});
      setCalcError(null);
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      prechargeToolApi.calculate(JSON.parse(payloadKey))
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setServerErrors(outcome.errors);
            setCalcError(outcome.errors.design || Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setServerErrors({});
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setServerErrors({});
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasClientErrors, payloadKey]);

  const errorFor = (key) => fieldErrors[key] || serverErrors[key];

  const handleCopy = async () => {
    if (!results) return;
    const r = results;
    const lines = [
      'HV Precharge Resistor Calculator',
      `Pack: ${raw.packVminV} / ${raw.packVnomV} / ${raw.packVmaxV} V (min/nom/max), R_int ${raw.battRintMohm} mΩ, loop ${raw.loopRmohm} mΩ`,
      `Criterion: ${criterion === 'percent' ? `V_link ≥ ${raw.thresholdPercent}% V_pack` : `V_pack − V_link ≤ ${raw.deltaVV} V`}`,
      `Capacitance: C_X ${fmt(r.capacitance.cXUf, 1)} µF, C_Y+ ${fmt(r.capacitance.cYPosNf, 0)} nF, C_Y− ${fmt(r.capacitance.cYNegNf, 0)} nF, C_total ${fmt(r.capacitance.cTotalUf, 2)} µF`,
      r.sizing
        ? `Resistor: exact ${fmtOhm(r.sizing.exactOhm)}, E12 ${fmtOhm(r.sizing.e12Ohm)}, E24 ${fmtOhm(r.sizing.e24Ohm)} -> using ${fmtOhm(r.resistor.selectedOhm)} (${r.sizing.series})`
        : `Resistor: ${fmtOhm(r.resistor.selectedOhm)} (±${raw.resTolPercent}%)`,
      `τ (nominal): ${fmt(r.nominal.tauMs, 1)} ms`,
      `t_pc: nominal ${fmt(r.nominal.tPcMs, 1)} ms, slow ${fmt(r.corners.slow.tPcMs, 1)} ms, fast ${fmt(r.corners.fast.tPcMs, 1)} ms`,
      `Suggested timeout: ${fmt(r.timeoutMs, 0)} ms (slow corner × ${raw.timeoutMargin})`,
      `Peak current / power @ V_max: ${fmt(r.stresses.iPeakA, 2)} A / ${fmt(r.stresses.pPeakW / 1000, 2)} kW`,
      `Resistor energy: ${fmt(r.stresses.energyPerAttemptJ, 2)} J per attempt, ${fmt(r.stresses.energyNAttemptsJ, 1)} J for ${r.stresses.attempts} attempts`,
      `Main contactor inrush at close: ${fmt(r.inrush.currentA, 0)} A (ΔV ${fmt(r.inrush.deltaVV, 1)} V)`,
      `Y touch energy @ ${r.yEnergy.atVoltageV} V: Y+ ${fmt(r.yEnergy.posJ, 3)} J ${r.yEnergy.posOk ? 'PASS' : 'FAIL'}, Y− ${fmt(r.yEnergy.negJ, 3)} J ${r.yEnergy.negOk ? 'PASS' : 'FAIL'} (limit 0.2 J)`,
      ...(r.warnings.length ? ['', 'Warnings:', ...r.warnings.map((w) => `  - ${w.message}`)] : []),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const numField = (key, { tip, label, disabled } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = errorFor(key);
    const id = `pc-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{label || spec.label}</FieldLabel>
        <Input
          id={id} type="number" step="any" value={raw[key]} disabled={disabled}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono" placeholder={spec.optional ? 'optional' : undefined}
          aria-invalid={!!error} data-testid={`${id}-input`}
        />
        {error && <ErrorText testId={`${id}-error`}>{error}</ErrorText>}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="technical-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                  <BatteryCharging className="h-5 w-5 text-autoxium-blue" />
                  HV Precharge Resistor Calculator
                </CardTitle>
                <CardDescription>
                  Size or check the precharge resistor for an EV HV bus from the X and Y
                  capacitance of every component on it. Results update live as you edit.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleExample} data-testid="pc-example-btn">
                  <Sparkles className="h-3.5 w-3.5" /> Example
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!results} data-testid="pc-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleReset} data-testid="pc-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <CircuitCard results={results} />

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <Card className="technical-card lg:col-span-3 min-w-0">
            <CardContent className="pt-6 space-y-6">
              <Section
                title="1. HV System"
                right={(
                  <Select value={preset} onValueChange={handlePreset}>
                    <SelectTrigger className="w-full sm:w-56 h-8 text-xs" data-testid="pc-preset-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {HV_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {numField('packVminV', { tip: 'Lowest pack voltage the precharge must work at (empty pack, cold).' })}
                  {numField('packVnomV', { tip: 'Nominal pack voltage. Sizing is done at this voltage.' })}
                  {numField('packVmaxV', { tip: 'Highest pack voltage (full charge). Stresses and Y-energy are evaluated here.' })}
                </div>
                {errorFor('voltages') && <ErrorText testId="pc-voltages-error">{errorFor('voltages')}</ErrorText>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {numField('battRintMohm', { tip: 'Pack DC internal resistance. It adds to the precharge resistor in the charging path, and limits the inrush when the main contactor closes.' })}
                  {numField('loopRmohm', { tip: 'Cables + contactors + fuse in the main path. Used only for the main-contactor inrush at the end of precharge.' })}
                </div>
              </Section>

              <Section title="2. E-Powertrain Components on the Bus">
                <ComponentEditor rows={rows} errorFor={errorFor} updateRow={updateRow} removeRow={removeRow} addRow={addRow} />
              </Section>

              <Section title="3. Design Mode">
                <Tabs value={mode} onValueChange={setMode}>
                  <TabsList className="grid grid-cols-2 w-full sm:w-80">
                    <TabsTrigger value="size" data-testid="pc-mode-size">Size resistor</TabsTrigger>
                    <TabsTrigger value="check" data-testid="pc-mode-check">Check resistor</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {mode === 'size' ? (
                    <>
                      {numField('targetTimeMs', { tip: 'Precharge time you want at nominal voltage, capacitance and resistance.' })}
                      <div className="space-y-2">
                        <FieldLabel tip="The exact R is rounded to the nearest value of this series, and every result is recalculated with the rounded value.">Standard Series</FieldLabel>
                        <Select value={eSeries} onValueChange={setESeries}>
                          <SelectTrigger data-testid="pc-eseries-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {E_SERIES_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : (
                    <>
                      {numField('resistorOhm', { tip: 'Nominal precharge resistance to check.' })}
                      {numField('targetTimeMs', { label: 'Max Precharge Time', tip: 'Optional. If given, you get a warning when the nominal precharge time is longer.' })}
                    </>
                  )}
                </div>
              </Section>

              <Section title="4. Completion Criterion">
                <Tabs value={criterion} onValueChange={setCriterion}>
                  <TabsList className="grid grid-cols-2 w-full sm:w-80">
                    <TabsTrigger value="percent" data-testid="pc-criterion-percent">% of V_pack</TabsTrigger>
                    <TabsTrigger value="deltaV" data-testid="pc-criterion-deltav">ΔV to V_pack</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {criterion === 'percent'
                    ? numField('thresholdPercent', { tip: 'Precharge is complete when the DC-link voltage reaches this share of the pack voltage. 95% (3τ) is common.' })
                    : numField('deltaVV', { tip: 'Precharge is complete when the DC link is within this many volts of the pack. With this criterion the time depends on the pack voltage.' })}
                </div>
              </Section>

              <Accordion type="single" collapsible defaultValue="tol" className="border border-structure rounded-md px-4 bg-muted/30">
                <AccordionItem value="tol" className="border-none">
                  <AccordionTrigger className="hover:no-underline py-3" data-testid="pc-tolerances-toggle">
                    <span className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">5. Tolerances &amp; Ratings</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      {numField('capTolPercent', { tip: 'Applied ± to the total capacitance for the slow (C_max) and fast (C_min) corners.' })}
                      {numField('capAgeingPercent', { tip: 'Optional extra end-of-life capacitance loss (film capacitors lose a few % over life). Lowers C_min only.' })}
                      {numField('resTolPercent', { tip: 'Applied ± to the precharge resistor for the corners.' })}
                      {numField('attempts', { tip: 'Precharge attempts back-to-back without cooling, for the resistor energy check.' })}
                      {numField('timeoutMargin', { tip: 'Suggested controller timeout = slow-corner precharge time × this factor. Engineering practice, not a standard.' })}
                      {numField('resistorEnergyRatingJ', { tip: 'Optional. Resistor single-pulse energy rating from its datasheet (for the pulse length of one precharge).' })}
                      {numField('resistorVoltageRatingV', { tip: 'Optional. Resistor maximum working voltage.' })}
                      {numField('contactorMakeRatingA', { tip: 'Optional. Main contactor make (inrush) current rating.' })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4 min-w-0">
            {calcError ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <div className="flex items-start gap-2 text-sm text-destructive" data-testid="pc-calc-error">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{calcError}</span>
                </div>
              </CardContent></Card>
            ) : hasClientErrors ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="pc-results-placeholder">
                  {fieldErrors.components || 'Fix the highlighted fields to see results.'}
                </p>
              </CardContent></Card>
            ) : !results ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="pc-results-loading">Calculating…</p>
              </CardContent></Card>
            ) : (
              <Summary r={results} attempts={raw.attempts} />
            )}
          </div>
        </div>

        {results && !calcError && !hasClientErrors && (
          <>
            <PrechargeCurveChart r={results} />
            <div className="grid md:grid-cols-2 gap-6">
              <StressChart r={results} kind="current" />
              <StressChart r={results} kind="power" />
            </div>
            <EnergyChart r={results} />
            <CapacitanceCard r={results} />
            <VoltageCornerTable r={results} />
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

// ---------------------------------------------------------------------------
// Circuit schematic + precharge sequence
// ---------------------------------------------------------------------------

// Labels are written as static <text> in CircuitCard, never as a dynamic {label} child of
// a sub-component: the dev-only visual-edits Babel plugin wraps dynamic children in an
// HTML <span>, which is invalid inside SVG and silently drops the text (same issue as
// simulator/ReferenceLineTopLabel.jsx).
const Contactor = ({ x, y }) => (
  <g>
    <line x1={x} y1={y} x2={x + 12} y2={y} />
    <line x1={x + 12} y1={y} x2={x + 38} y2={y - 12} />
    <line x1={x + 40} y1={y} x2={x + 52} y2={y} />
    <circle cx={x + 12} cy={y} r="2.5" className="fill-current" />
    <circle cx={x + 40} cy={y} r="2.5" className="fill-current" />
  </g>
);

const Resistor = ({ x, y, w = 44, vertical }) => (vertical
  ? <rect x={x - 7} y={y} width="14" height={w} className="fill-background" />
  : <rect x={x} y={y - 7} width={w} height="14" className="fill-background" />);

const CircuitCard = ({ results }) => {
  const tpc = results ? `${fmt(results.nominal.tPcMs, 0)} ms` : 't_pc';
  const steps = [
    { n: 1, title: 'Close main−', note: 'Bus negative connected' },
    { n: 2, title: 'Close precharge', note: 'C charges through R_pre' },
    { n: 3, title: 'Wait for V_th', note: `≈ ${tpc} nominal` },
    { n: 4, title: 'Close main+', note: 'Small ΔV inrush' },
    { n: 5, title: 'Open precharge', note: 'R_pre bypassed' },
  ];
  return (
    <Card className="technical-card" data-testid="pc-circuit">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Circuit Modelled &amp; Precharge Sequence</CardTitle>
        <CardDescription>Battery (+ R_int) and main−, with the precharge contactor + R_pre in parallel with main+, feeding the HV bus (C_X, Y+/Y− to chassis, bleed).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="w-full overflow-x-auto" data-testid="pc-schematic-scroll">
          <div className="min-w-[520px] max-w-3xl mx-auto text-foreground">
          <svg viewBox="0 0 600 230" className="w-full h-auto" role="img" aria-label="Precharge circuit schematic" stroke="currentColor" strokeWidth="1.6" fill="none">
            {/* Battery */}
            <line x1="40" y1="70" x2="40" y2="102" />
            <line x1="24" y1="102" x2="56" y2="102" strokeWidth="3" />
            <line x1="31" y1="112" x2="49" y2="112" strokeWidth="3" />
            <line x1="40" y1="112" x2="40" y2="170" />
            <text x="8" y="140" className="fill-current" stroke="none" fontSize="12">V_pack</text>
            <text x="46" y="96" className="fill-current" stroke="none" fontSize="11">+</text>
            {/* Top rail: battery+ -> R_int -> node A */}
            <line x1="40" y1="70" x2="70" y2="70" />
            <Resistor x={70} y={70} w={40} />
            <text x="90" y="56" textAnchor="middle" className="fill-current" stroke="none" fontSize="12">R_int</text>
            <line x1="110" y1="70" x2="200" y2="70" />
            <circle cx="200" cy="70" r="3" className="fill-current" />
            {/* main+ */}
            <Contactor x={200} y={70} />
            <text x="226" y="90" textAnchor="middle" className="fill-current" stroke="none" fontSize="12">main+</text>
            <line x1="252" y1="70" x2="330" y2="70" />
            <circle cx="330" cy="70" r="3" className="fill-current" />
            {/* Precharge branch */}
            <line x1="200" y1="70" x2="200" y2="28" />
            <line x1="200" y1="28" x2="206" y2="28" />
            <Contactor x={194} y={28} />
            <text x="220" y="12" textAnchor="middle" className="fill-current" stroke="none" fontSize="12">precharge</text>
            <line x1="246" y1="28" x2="262" y2="28" />
            <Resistor x={262} y={28} w={44} />
            <text x="284" y="14" textAnchor="middle" className="fill-current" stroke="none" fontSize="12">R_pre</text>
            <line x1="306" y1="28" x2="330" y2="28" />
            <line x1="330" y1="28" x2="330" y2="70" />
            {/* Bottom rail with main- */}
            <line x1="40" y1="170" x2="200" y2="170" />
            <Contactor x={200} y={170} />
            <text x="226" y="190" textAnchor="middle" className="fill-current" stroke="none" fontSize="12">main−</text>
            <line x1="252" y1="170" x2="560" y2="170" />
            <line x1="330" y1="70" x2="560" y2="70" />
            <text x="340" y="60" className="fill-current" stroke="none" fontSize="11">HV+ (V_link)</text>
            <text x="340" y="190" className="fill-current" stroke="none" fontSize="11">HV−</text>
            {/* X capacitor */}
            <line x1="390" y1="70" x2="390" y2="114" />
            <line x1="376" y1="114" x2="404" y2="114" strokeWidth="3" />
            <line x1="376" y1="124" x2="404" y2="124" strokeWidth="3" />
            <line x1="390" y1="124" x2="390" y2="170" />
            <text x="408" y="123" className="fill-current" stroke="none" fontSize="12">C_X</text>
            {/* Bleed resistor */}
            <line x1="455" y1="70" x2="455" y2="98" />
            <Resistor x={455} y={98} w={44} vertical />
            <text x="467" y="124" className="fill-current" stroke="none" fontSize="12">R_bleed</text>
            <line x1="455" y1="142" x2="455" y2="170" />
            {/* Y capacitors to chassis */}
            <line x1="545" y1="70" x2="545" y2="88" />
            <line x1="533" y1="88" x2="557" y2="88" strokeWidth="3" />
            <line x1="533" y1="96" x2="557" y2="96" strokeWidth="3" />
            <line x1="545" y1="96" x2="545" y2="120" />
            <line x1="545" y1="120" x2="545" y2="144" />
            <line x1="533" y1="144" x2="557" y2="144" strokeWidth="3" />
            <line x1="533" y1="152" x2="557" y2="152" strokeWidth="3" />
            <line x1="545" y1="152" x2="545" y2="170" />
            <line x1="545" y1="120" x2="575" y2="120" />
            <line x1="575" y1="120" x2="575" y2="130" />
            <line x1="565" y1="130" x2="585" y2="130" />
            <line x1="568" y1="135" x2="582" y2="135" />
            <line x1="571" y1="140" x2="579" y2="140" />
            <text x="520" y="84" textAnchor="end" className="fill-current" stroke="none" fontSize="11">Y+</text>
            <text x="520" y="160" textAnchor="end" className="fill-current" stroke="none" fontSize="11">Y−</text>
            <text x="590" y="156" textAnchor="end" className="fill-current" stroke="none" fontSize="10">chassis</text>
          </svg>
          </div>
        </div>
        <ol className="grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="pc-sequence">
          {steps.map((s) => (
            <li key={s.n} className="flex sm:flex-col items-start gap-2 rounded-md border border-structure bg-muted/40 p-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-autoxium-blue text-white font-mono text-xs font-bold">{s.n}</span>
              <span className="min-w-0">
                <span className="block font-mono text-xs font-bold uppercase tracking-wide">{s.title}</span>
                <span className="block text-xs text-muted-foreground">{s.note}</span>
              </span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Component editor (grid rows on desktop, stacked cards on mobile — no raw <table>)
// ---------------------------------------------------------------------------

const EDITOR_GRID = 'md:grid md:grid-cols-[2.25rem_minmax(0,2.6fr)_repeat(5,minmax(0,1fr))_2rem] md:items-start md:gap-2';

const ComponentEditor = ({ rows, errorFor, updateRow, removeRow, addRow }) => (
  <div className="space-y-2" data-testid="pc-components">
    <p className="flex items-start gap-1.5 text-xs text-amber-700">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      Default values are typical — replace with supplier data. Qty multiplies every column; leave Bleed blank if the component has no passive discharge resistor.
    </p>
    <div className={`hidden ${EDITOR_GRID} px-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground`}>
      <span>On</span><span>Component</span><span>Qty</span>
      {['X', 'Y+', 'Y−', 'Bleed'].map((h, k) => (
        <span key={h}>{h} <span className="normal-case">({['µF', 'nF', 'nF', 'kΩ'][k]})</span></span>
      ))}
      <span />
    </div>
    {rows.map((row, i) => (
      <div
        key={row.id}
        className={`${EDITOR_GRID} grid grid-cols-2 gap-2 rounded-md border border-structure bg-background p-2 ${row.included ? '' : 'opacity-60'}`}
        data-testid={`pc-comp-row-${i}`}
      >
        <div className="col-span-2 md:col-span-1 flex items-center gap-2 md:pt-2">
          <Switch
            checked={row.included}
            onCheckedChange={(v) => updateRow(row.id, 'included', v)}
            aria-label={`Include ${row.name}`}
            data-testid={`pc-comp-${i}-included`}
          />
          <span className="md:hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Included</span>
          <button
            type="button" onClick={() => removeRow(row.id)}
            className="md:hidden ml-auto text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${row.name}`} data-testid={`pc-comp-${i}-remove-mobile`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="col-span-2 md:col-span-1 space-y-1">
          <Label className="md:sr-only font-mono text-[10px] uppercase tracking-wider text-muted-foreground" htmlFor={`pc-comp-${i}-name`}>Component</Label>
          <Input
            id={`pc-comp-${i}-name`} value={row.name} maxLength={80}
            onChange={(e) => updateRow(row.id, 'name', e.target.value)}
            className="h-9 text-xs" data-testid={`pc-comp-${i}-name`}
          />
        </div>
        {COMPONENT_FIELD_SPECS.map((spec) => {
          const err = errorFor(`components.${i}.${spec.key}`);
          const id = `pc-comp-${i}-${spec.key}`;
          return (
            <div key={spec.key} className="space-y-1 min-w-0">
              <Label className="md:sr-only font-mono text-[10px] uppercase tracking-wider text-muted-foreground" htmlFor={id}>
                {spec.label}{spec.unit && <span className="normal-case"> ({spec.unit})</span>}
              </Label>
              <Input
                id={id} type="number" step="any" value={row[spec.key]}
                placeholder={spec.optional ? '—' : undefined}
                onChange={(e) => updateRow(row.id, spec.key, e.target.value)}
                className="h-9 font-mono text-xs px-2" aria-invalid={!!err} data-testid={id}
              />
              {err && <p className="text-[10px] leading-tight text-destructive" data-testid={`${id}-error`}>{err}</p>}
            </div>
          );
        })}
        <div className="hidden md:flex md:pt-2 justify-center">
          <button
            type="button" onClick={() => removeRow(row.id)}
            className="text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${row.name}`} data-testid={`pc-comp-${i}-remove`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    ))}
    {errorFor('components') && <ErrorText testId="pc-components-error">{errorFor('components')}</ErrorText>}
    <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={rows.length >= MAX_COMPONENTS} data-testid="pc-add-component">
      <Plus className="h-3.5 w-3.5" /> Add component
    </Button>
  </div>
);

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

const Stat = ({ label, value, sub, testId, tone }) => (
  <div className="min-w-0">
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className={`font-mono text-xl font-black break-words ${tone || 'text-autoxium-blue'}`} data-testid={testId}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const Row = ({ label, children, testId }) => (
  <div className="flex items-baseline justify-between gap-3 text-xs font-mono py-1 border-b border-structure/60 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-bold text-right" data-testid={testId}>{children}</span>
  </div>
);

const PassBadge = ({ ok, children, testId }) => (
  <Badge
    variant="outline"
    className={`font-mono text-[11px] gap-1 ${ok ? 'text-emerald-700 border-emerald-600/40' : 'text-destructive border-destructive/40'}`}
    data-testid={testId}
  >
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    {children}
  </Badge>
);

const Summary = ({ r }) => {
  const s = r.stresses;
  const warnings = r.warnings.filter((w) => w.level === 'warning');
  const infos = r.warnings.filter((w) => w.level !== 'warning');
  return (
    <div className="space-y-4" data-testid="pc-results">
      <Card className={`technical-card ring-1 ${warnings.length ? 'ring-amber-500/40' : 'ring-emerald-600/30'}`}>
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">
            {r.sizing ? 'Required Resistor' : 'Selected Resistor'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {r.sizing ? (
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Exact" value={`${fmt(r.sizing.exactOhm, 2)} Ω`} testId="pc-r-exact" tone={r.sizing.series === 'exact' ? undefined : 'text-foreground'} />
              <Stat label="E12" value={fmtOhm(r.sizing.e12Ohm)} testId="pc-r-e12" tone={r.sizing.series === 'E12' ? undefined : 'text-foreground'} />
              <Stat label="E24" value={fmtOhm(r.sizing.e24Ohm)} testId="pc-r-e24" tone={r.sizing.series === 'E24' ? undefined : 'text-foreground'} />
            </div>
          ) : (
            <Stat label="R_pre (nominal)" value={fmtOhm(r.resistor.selectedOhm)} sub={`Corners: ${fmtOhm(r.resistor.minOhm)} – ${fmtOhm(r.resistor.maxOhm)}`} testId="pc-r-selected" />
          )}
          {r.sizing && (
            <p className="text-xs text-muted-foreground" data-testid="pc-r-used">
              All results below use <span className="font-bold text-foreground">{fmtOhm(r.resistor.selectedOhm)}</span>
              {r.sizing.series !== 'exact' && ` (${r.sizing.series})`}. The exact value gives {fmt(r.sizing.exactTpcMs, 1)} ms ({r.sizing.method}).
            </p>
          )}
          <div className="grid grid-cols-2 gap-4 pt-3 border-t border-structure">
            <Stat label="t_pc nominal" value={`${fmt(r.nominal.tPcMs, 1)} ms`} testId="pc-tpc-nom" sub={`slow ${fmt(r.corners.slow.tPcMs, 1)} · fast ${fmt(r.corners.fast.tPcMs, 1)} ms`} />
            <Stat label="Suggested timeout" value={`${fmt(r.timeoutMs, 0)} ms`} testId="pc-timeout" sub="slow corner × margin (practice)" />
          </div>
        </CardContent>
      </Card>

      {(warnings.length > 0 || infos.length > 0) && (
        <Card className="technical-card" data-testid="pc-warnings">
          <CardContent className="pt-6 space-y-2">
            {warnings.map((w) => (
              <div key={w.code + w.message} className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-800 leading-relaxed" data-testid={`pc-warning-${w.code}`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w.message}
              </div>
            ))}
            {infos.map((w) => (
              <div key={w.code + w.message} className="flex items-start gap-2 rounded-md bg-muted/60 p-2.5 text-xs text-muted-foreground leading-relaxed" data-testid={`pc-warning-${w.code}`}>
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w.message}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="technical-card">
        <CardHeader><CardTitle className="font-chivo uppercase tracking-tight text-sm">Timing &amp; Stresses</CardTitle></CardHeader>
        <CardContent>
          <Row label="τ nominal" testId="pc-tau">{fmt(r.nominal.tauMs, 2)} ms</Row>
          <Row label={`Slow corner (C_max, R_max) @ ${r.corners.slow.vCorner}`}>{fmt(r.corners.slow.tPcMs, 1)} ms</Row>
          <Row label={`Fast corner (C_min, R_min) @ ${r.corners.fast.vCorner}`}>{fmt(r.corners.fast.tPcMs, 1)} ms</Row>
          <Row label="Threshold V_th @ V_nom">{fmt(r.nominal.vThV, 1)} V</Row>
          {r.capacitance.rBleedKohm !== null && <Row label="V_final (bleed-limited) @ V_nom">{fmt(r.nominal.vFinalV, 1)} V</Row>}
          <Row label="Peak current @ V_max (R_min)" testId="pc-ipeak">{fmt(s.iPeakA, 2)} A</Row>
          <Row label="Peak resistor power" testId="pc-ppeak">{fmt(s.pPeakW / 1000, 2)} kW</Row>
          <Row label={`Energy / attempt @ V_max (${s.energyCorner})`} testId="pc-energy">{fmt(s.energyPerAttemptJ, 2)} J</Row>
          <Row label={`Energy × ${s.attempts} attempts`} testId="pc-energy-n">
            {fmt(s.energyNAttemptsJ, 1)} J
            {s.energyOk !== null && <span className={s.energyOk ? 'text-emerald-700' : 'text-destructive'}> ({s.energyOk ? '≤' : '>'} {s.energyRatingJ} J)</span>}
          </Row>
          <Row label="Resistor peak voltage">
            {fmt(s.resistorPeakV, 0)} V
            {s.voltageOk !== null && <span className={s.voltageOk ? 'text-emerald-700' : 'text-destructive'}> (rating {s.voltageRatingV} V)</span>}
          </Row>
          <Row label={`Main-contactor inrush @ ${r.inrush.vCorner}`} testId="pc-inrush">
            {fmt(r.inrush.currentA, 0)} A
            {r.inrush.ok !== null && <span className={r.inrush.ok ? 'text-emerald-700' : 'text-destructive'}> (rating {r.inrush.ratingA} A)</span>}
          </Row>
          <Row label="Shorted-link energy until timeout (fault, info)">{fmt(s.shortedLinkEnergyJ, 0)} J</Row>
        </CardContent>
      </Card>

      <Card className="technical-card">
        <CardHeader><CardTitle className="font-chivo uppercase tracking-tight text-sm">Capacitance &amp; Y-Energy</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Row label="C_X total" testId="pc-cx">{fmt(r.capacitance.cXUf, 1)} µF</Row>
            <Row label="C_Y+ / C_Y−">{fmt(r.capacitance.cYPosNf, 0)} / {fmt(r.capacitance.cYNegNf, 0)} nF</Row>
            <Row label="C_Y effective (Y+ in series with Y−)">{fmt(r.capacitance.cYEffNf, 0)} nF ({fmt(r.capacitance.ySharePpm / 10000, 3)}% of total)</Row>
            <Row label="C_total" testId="pc-ctotal">{fmt(r.capacitance.cTotalUf, 2)} µF</Row>
            <Row label="Bleed (parallel)">{r.capacitance.rBleedKohm === null ? 'none' : `${fmt(r.capacitance.rBleedKohm, 1)} kΩ`}</Row>
          </div>
          <div className="flex flex-wrap gap-2">
            <PassBadge ok={r.yEnergy.posOk} testId="pc-y-pos">Y+ {fmt(r.yEnergy.posJ, 3)} J</PassBadge>
            <PassBadge ok={r.yEnergy.negOk} testId="pc-y-neg">Y− {fmt(r.yEnergy.negJ, 3)} J</PassBadge>
          </div>
          <p className="text-xs text-muted-foreground">
            ½·C_Y·V_max² at {r.yEnergy.atVoltageV} V vs the ISO 6469-3 0.2 J limit (≤ {fmt(r.yEnergy.maxPerSideNf, 0)} nF per side at this voltage).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Charts (series come from the backend)
// ---------------------------------------------------------------------------

// Round an axis maximum up to 1/2/5 x 10^n so the top tick is a clean number.
const niceCeil = (v) => {
  if (!(v > 0)) return 1;
  const step = 10 ** Math.floor(Math.log10(v)) / 2;
  return Math.ceil(v / step) * step;
};

// Left-anchored label for a horizontal reference line, drawn just above (or below) it.
// Uses Recharts' <Text> for the same reason as ReferenceLineTopLabel (no dev <span> wrap).
const HLineLabel = ({ viewBox, text, color, below }) => {
  if (!viewBox) return null;
  return (
    <Text
      x={viewBox.x + 6} y={below ? viewBox.y + 4 : viewBox.y - 4}
      verticalAnchor={below ? 'start' : 'end'} textAnchor="start"
      fill={color} fontSize={11} fontFamily={AXIS_LABEL_STYLE.fontFamily}
    >
      {text}
    </Text>
  );
};

const msTick = (v) => (v >= 100 ? Math.round(v).toLocaleString() : Number(v).toFixed(v >= 10 ? 0 : 1));

const ChartCard = ({ title, description, children, testId }) => (
  <Card className="technical-card min-w-0" data-testid={testId}>
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-sm">{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const timeAxis = (zoom) => (
  <XAxis
    dataKey="tMs" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
    tickFormatter={msTick}
    label={{ value: 'Time (ms)', position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }}
  />
);

const PrechargeCurveChart = ({ r }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const m = r.chartMeta;
  const data = r.chart;
  return (
    <ChartCard
      title="Precharge Curve — V_link vs Time"
      description={`Nominal curve (solid) with slow and fast tolerance corners (dashed), all at V_nom. t_pc = ${fmt(m.tPcNomMs, 1)} ms, suggested timeout = ${fmt(m.timeoutMs, 0)} ms. Past t_pc the main contactor closes; the curves continue as the RC response would if it did not.`}
      testId="pc-chart-vlink"
    >
      <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-pc-vlink" />
      <div className="h-80 md:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 24, left: 4, bottom: 24 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            {timeAxis(zoom)}
            <YAxis tick={AXIS_TICK_STYLE} width={52} domain={[0, (max) => niceCeil(Math.max(max, m.vPackNomV) * 1.05)]}
              label={{ value: 'V_link (V)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} />
            <RechartsTooltip
              contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v, name) => [`${fmt(v, 1)} V`, name]} labelFormatter={(v) => `${fmt(v, 1)} ms`}
            />
            <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
            {m.tauMarkersMs.map((t, k) => (
              <ReferenceLine key={t} x={t} stroke={CHART_AXIS} strokeDasharray="2 4"
                label={{ value: `${k + 1}τ`, position: 'insideBottomLeft', fill: CHART_TEXT, fontSize: 10 }} />
            ))}
            <ReferenceLine y={m.vPackNomV} stroke={MARK_DARK} strokeDasharray="6 3"
              label={<HLineLabel text={`V_pack ${fmt(m.vPackNomV, 0)} V`} color={MARK_DARK} />} />
            <ReferenceLine y={m.vThNomV} stroke={MARK_RED} strokeDasharray="3 3"
              label={<HLineLabel text={`V_th ${fmt(m.vThNomV, 0)} V`} color={MARK_RED} below />} />
            <ReferenceLine x={m.tPcNomMs} stroke={MARK_RED} strokeDasharray="4 4"
              label={<ReferenceLineTopLabel text="t_pc" color={MARK_RED} anchor={referenceLabelAnchorFor(m.tPcNomMs, zoom.xDomain, data, 'tMs')} />} />
            <ReferenceLine x={m.timeoutMs} stroke={MARK_DARK} strokeDasharray="4 4"
              label={<ReferenceLineTopLabel text="timeout" color={MARK_DARK} anchor={referenceLabelAnchorFor(m.timeoutMs, zoom.xDomain, data, 'tMs')} />} />
            <Line type="monotone" dataKey="vNom" name="Nominal" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
            <Line type="monotone" dataKey="vSlow" name="Slow corner (C_max, R_max)" stroke={SERIES_COLORS[1]} strokeDasharray="6 4" {...LINE_PROPS} />
            <Line type="monotone" dataKey="vFast" name="Fast corner (C_min, R_min)" stroke={SERIES_COLORS[2]} strokeDasharray="2 3" {...LINE_PROPS} />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

const StressChart = ({ r, kind }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const m = r.chartMeta;
  const isCurrent = kind === 'current';
  const unit = isCurrent ? 'A' : 'kW';
  return (
    <ChartCard
      title={isCurrent ? 'Precharge Current vs Time' : 'Resistor Power vs Time'}
      description={`Nominal at V_nom (solid) and worst case — fast corner at V_max ${fmt(m.vMaxV, 0)} V (dashed).`}
      testId={`pc-chart-${kind}`}
    >
      <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-pc-${kind}`} />
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={r.chart} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 24, left: 4, bottom: 24 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            {timeAxis(zoom)}
            <YAxis tick={AXIS_TICK_STYLE} width={52}
              label={{ value: isCurrent ? 'Current (A)' : 'Power (kW)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} />
            <RechartsTooltip
              contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v, name) => [`${fmt(v, isCurrent ? 2 : 3)} ${unit}`, name]} labelFormatter={(v) => `${fmt(v, 1)} ms`}
            />
            <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
            <ReferenceLine x={m.tPcNomMs} stroke={MARK_RED} strokeDasharray="4 4"
              label={<ReferenceLineTopLabel text="t_pc" color={MARK_RED} anchor={referenceLabelAnchorFor(m.tPcNomMs, zoom.xDomain, r.chart, 'tMs')} />} />
            <Line type="monotone" dataKey={isCurrent ? 'iNom' : 'pNomKw'} name="Nominal" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
            <Line type="monotone" dataKey={isCurrent ? 'iWorst' : 'pWorstKw'} name="Worst case" stroke={SERIES_COLORS[2]} strokeDasharray="6 4" {...LINE_PROPS} />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

const EnergyChart = ({ r }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const m = r.chartMeta;
  const rating = r.stresses.energyRatingJ;
  return (
    <ChartCard
      title="Cumulative Resistor Energy vs Time"
      description={`Nominal at V_nom (solid) and the worst energy corner (${r.stresses.energyCorner} at V_max, dashed). Dots mark the energy at each curve's own t_pc = energy per attempt.${rating ? ` The rating line is for one pulse; ${r.stresses.attempts} back-to-back attempts total ${fmt(r.stresses.energyNAttemptsJ, 1)} J.` : ''}`}
      testId="pc-chart-energy"
    >
      <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-pc-energy" />
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={r.chart} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 24, left: 4, bottom: 24 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            {timeAxis(zoom)}
            <YAxis tick={AXIS_TICK_STYLE} width={52} domain={[0, (max) => niceCeil(Math.max(max, rating ? rating * 1.08 : 0))]}
              label={{ value: 'Energy (J)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} />
            <RechartsTooltip
              contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v, name) => [`${fmt(v, 2)} J`, name]} labelFormatter={(v) => `${fmt(v, 1)} ms`}
            />
            <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
            {rating && (
              <ReferenceLine y={rating} stroke={MARK_RED} strokeDasharray="6 3"
                label={<HLineLabel text={`Pulse rating ${rating} J`} color={MARK_RED} />} />
            )}
            <Line type="monotone" dataKey="eNom" name="Nominal" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
            <Line type="monotone" dataKey="eWorst" name="Worst energy corner" stroke={SERIES_COLORS[1]} strokeDasharray="6 4" {...LINE_PROPS} />
            <ReferenceDot x={m.tPcNomMs} y={r.nominal.energyJ} r={5} fill={SERIES_COLORS[0]} stroke="#fff" strokeWidth={2} ifOverflow="discard" />
            <ReferenceDot x={m.energyWorstTpcMs} y={r.stresses.energyPerAttemptJ} r={5} fill={SERIES_COLORS[1]} stroke="#fff" strokeWidth={2} ifOverflow="discard" />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

// Middle ellipsis keeps look-alike names distinct ("Traction …(front)" vs "Traction …ar, AWD)").
const truncateMiddle = (s, n) => (s.length > n ? `${s.slice(0, Math.ceil((n - 1) / 2))}…${s.slice(-Math.floor((n - 1) / 2))}` : s);

const CapacitanceCard = ({ r }) => {
  const bars = r.breakdown.filter((b) => b.xTotalUf > 0);
  return (
    <ChartCard
      title="Capacitance Contribution per Component"
      description="X capacitance (µF) of each included component, and the Y+/Y− capacitance to chassis with each component's share of C_total."
      testId="pc-capacitance"
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div style={{ height: Math.max(160, bars.length * 34 + 40) }} data-testid="pc-cap-bar">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 16 }}>
              <CartesianGrid stroke={CHART_GRID} horizontal={false} />
              <XAxis type="number" tick={AXIS_TICK_STYLE}
                label={{ value: 'X capacitance (µF)', position: 'insideBottom', offset: -8, style: AXIS_LABEL_STYLE }} />
              <YAxis type="category" dataKey="name" tick={AXIS_TICK_STYLE} width={120} tickFormatter={(s) => truncateMiddle(s, 17)} interval={0} />
              <RechartsTooltip
                contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                formatter={(v) => [`${fmt(v, 1)} µF`, 'X (qty × X)']}
              />
              <Bar dataKey="xTotalUf" name="X (µF)" fill={SERIES_COLORS[0]} radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto" data-testid="pc-breakdown-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Component</TableHead>
                <TableHead className="text-right">X µF</TableHead>
                <TableHead className="text-right">Share</TableHead>
                <TableHead className="text-right">Y+ nF</TableHead>
                <TableHead className="text-right">Y− nF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.breakdown.map((b, i) => (
                <TableRow key={`${b.name}-${i}`}>
                  <TableCell className="text-xs">{b.name}{b.qty > 1 ? ` ×${b.qty}` : ''}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(b.xTotalUf, 1)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(b.sharePercent, 1)}%</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(b.yPosTotalNf, 0)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(b.yNegTotalNf, 0)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-bold">
                <TableCell className="text-xs font-bold">Total</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{fmt(r.capacitance.cXUf, 1)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{fmt(r.breakdown.reduce((a, b) => a + b.sharePercent, 0), 1)}%</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{fmt(r.capacitance.cYPosNf, 0)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{fmt(r.capacitance.cYNegNf, 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground mt-2">
            Shares are of C_total = C_X + C_Y,eff; the remaining {fmt(r.capacitance.ySharePpm / 10000, 3)}% is the series Y+/Y− path ({fmt(r.capacitance.cYEffNf, 0)} nF).
          </p>
        </div>
      </div>
    </ChartCard>
  );
};

const VoltageCornerTable = ({ r }) => (
  <ChartCard
    title="Voltage Corners (nominal R and C)"
    description="With the ΔV criterion the precharge time depends on pack voltage; with the % criterion it does not."
    testId="pc-voltage-corners"
  >
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Corner</TableHead>
            <TableHead className="text-right">V_pack</TableHead>
            <TableHead className="text-right">V_th</TableHead>
            <TableHead className="text-right">t_pc (ms)</TableHead>
            <TableHead className="text-right">I_peak (A)</TableHead>
            <TableHead className="text-right">E_R (J)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {r.voltageCorners.map((row) => (
            <TableRow key={row.corner} data-testid={`pc-vcorner-${row.corner}`}>
              <TableCell className="font-mono text-xs font-bold">{row.corner}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmt(row.vPackV, 1)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmt(row.vThV, 1)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmt(row.tPcMs, 1)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmt(row.iPeakA, 2)}</TableCell>
              <TableCell className="text-right font-mono text-xs">{fmt(row.energyJ, 2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </ChartCard>
);

export default PrechargeResistorCalculator;
